<template>
  <ui-component-modal
    :modalTitle="
      $t('Components.Yield.ModalCreateYieldSettings.Main_Title')
    "
    :isSaving="isSaving"
    :isSavingSuccess="isSavingSuccess"
    :isSavingError="isSavingError"
    :hideFooterButtons="isSavingError || isSavingSuccess"
    :onClickCancel="onClickCancel"
    :onClickSave="createYieldSetting"
    :disableSaveButton="
      disableSave
    "
    :showModal="showModal"
    modalSize="large"
    @closeModal="onClickCancel"
  >
    <template slot="modalTitle"></template>
    <template v-slot:content>
      <div>
        <Message class="has-margin-bottom">
          <div v-html="
              $t(
                'Components.Yield.ModalCreateYieldSettings.Message_InfoFirstRunCreate',
                { br: '<br>' }
              )
            "></div>
        </Message>
      </div>

      <table class="table-settings table is-fullwidth is-striped is-hoverable">
        <tbody>
          <tr
            v-if="voucherId > 0"
            class="has-text-vertical-middle"
          >
            <td>Voucher</td>
            <td>{{ voucher.Name }}</td>
          </tr>
          <tr
            v-if="voucherId === 0"
            class="has-text-vertical-middle"
          >
            <td>{{ $t('General.Channel') }}</td>
            <td>
              <div v-if="channelId > 0">{{ channelName }}</div>
              <div
                v-if="channelId === 0"
                class="select"
              >
                <select
                  @change="getChannelData"
                  v-model="yieldSettings.ChannelId"
                >
                  <option
                    :value="0"
                    :selected="yieldSettings.ChannelId === 0"
                  >
                    {{
                      $t(
                        'Components.Yield.ModalCreateYieldSettings.Label_SelectChannel'
                      )
                    }}
                  </option>
                  <option
                    v-for="(channel, index) in locationState.Channels"
                    :key="index"
                    :value="channel.ChannelId"
                    :selected="channel.ChannelId === yieldSettings.ChannelId"
                  >
                    {{ channel.ChannelName }}
                  </option>
                </select>
              </div>
            </td>
          </tr>
          <tr
            v-if="voucherId === 0"
            class="has-text-vertical-middle"
          >
            <td>
              {{
                $t(
                  'Components.Yield.ModalCreateYieldSettings.Label_MeetingType'
                )
              }}
            </td>
            <td>
              <div v-if="meetingtypeId > 0">
                {{ $t(`General.MeetingType${meetingtypeId}_Name`) }}
              </div>
              <div
                v-if="meetingtypeId === 0"
                class="select"
              >
                <select
                  @change="meetingtypeSelected"
                  v-model="yieldSettings.MeetingtypeId"
                >
                  <option :value="0">
                    {{
                      $t(
                        'Components.Yield.ModalCreateYieldSettings.Label_SelectMeetingType'
                      )
                    }}
                  </option>
                  <option
                    v-for="(meetingtype, index) in meetingtypes"
                    :key="index"
                    :value="meetingtype"
                    :selected="yieldSettings.MeetingtypeId === meetingtype"
                  >
                    {{ meetingtype | getMeetingtypeName }}
                  </option>
                </select>
              </div>
            </td>
          </tr>
          <tr
            v-if="voucherId === 0"
            class="has-text-vertical-middle"
          >
            <td>
              {{
                $t(
                  'Components.Yield.ModalCreateYieldSettings.Label_Space'
                )
              }}
            </td>
            <td>
              <div v-if="spaceId > 0">{{ space.Name }}</div>
              <div
                v-if="spaceId === 0"
                class="select"
                :class="{ 'is-loading': isLoadingSpaces }"
              >
                <select v-model="yieldSettings.SpaceId">
                  <option :value="0">
                    {{
                      $t(
                        'Components.Yield.ModalCreateYieldSettings.Label_AllSpaces'
                      )
                    }}
                  </option>
                  <option
                    v-for="(space, index) in spaces"
                    :key="index"
                    :value="space.Id"
                    :selected="yieldSettings.SpaceId === space.Id"
                  >
                    {{ space.Name }}
                  </option>
                </select>
              </div>
            </td>
          </tr>
          <tr
            v-if="voucherId === 0"
            class="has-text-vertical-middle"
          >
            <td>
              {{
                $t(
                  'Components.Yield.ModalCreateYieldSettings.Label_ValidFrom'
                )
              }}
            </td>
            <td>
              <BaseDateField
                v-model="startDate"
                :openCalendarInModal="true"
                class="customWidth"
              />
            </td>
          </tr>
          <tr class="has-text-vertical-middle">
            <td>
              {{
                $t(
                  'Components.Yield.ModalCreateYieldSettings.Label_TaxPercentage'
                )
              }}
            </td>
            <td>
              <div class="select">
                <select v-model="yieldSettings.TaxPercentage">
                  <option
                    v-for="(
                      taxPercentage, index
                    ) in locationState.TaxPercentages"
                    :key="index"
                    :value="taxPercentage.TaxPercentage"
                    :selected="
                      yieldSettings.TaxPercentage ===
                      taxPercentage.TaxPercentage
                    "
                  >
                    {{ taxPercentage.TaxPercentage }}%
                  </option>
                </select>
              </div>
            </td>
          </tr>

          <tr>
              <td>
                {{
                  $t(`Components.Yield.ModalCreateYieldSettings.Label_Deductible`)
                }}
              </td>
              <td>
                <div class="control">
                  <label class="radio">
                    <input type="radio" v-model="yieldSettings.Deductible" :checked="yieldSettings.Deductible" :value="true" name="deductible" />
                    {{ $t('General.Yes') }}
                  </label>
                  <label class="radio">
                    <input type="radio" v-model="yieldSettings.Deductible" :checked="!yieldSettings.Deductible" :value="false" name="deductible" />
                    {{ $t('General.No') }}
                  </label>
                </div>
              </td>
            </tr>

          <tr class="has-text-vertical-middle">
            <td>
              {{
                $t(
                  'Components.Yield.ModalCreateYieldSettings.Label_CalculationType'
                )
              }}
            </td>
            <td>
              <div v-if="channel && channel.PriceTypes.length === 1">
                {{
                  $t(
                    `Components.Yield.ModalCreateYieldSettings.${yieldSettings.CalculationType}`
                  )
                }}
              </div>

              <div
                v-if="
                  yieldSettings.ChannelId > 0 &&
                  channel &&
                  channel.PriceTypes.length > 1
                "
                class="select"
              >
                <select v-model="yieldSettings.CalculationType">
                  <option
                    v-for="(pct, index) in channel.PriceTypes"
                    :key="index"
                    :value="pct.PriceType"
                    :selected="yieldSettings.CalculationType === pct.PriceType"
                  >
                    {{ calculationTypeLabels[pct.PriceType] }}
                  </option>
                </select>
              </div>
            </td>
          </tr>

          <tr v-if="yieldSettings.MeetingtypeId === 1">
            <td>{{ $t('Components.Yield.ModalCreateYieldSettings.MinPeople') }}</td>
            <td>
              <ul class="tabs is-toggle is-inline-flex is-small">
                <li :class="{
                    'is-active': yieldSettings.MinPeople === 0,
                  }">
                  <a @click="yieldSettings.MinPeople = 0">
                    <span>{{ $t(`General.State_Off`) }}</span>
                  </a>
                </li>
                <li :class="{
                    'is-active': yieldSettings.MinPeople > 0,
                  }">
                  <a @click="yieldSettings.MinPeople = 1">
                    <span>{{ $t(`General.State_On`) }}</span>
                  </a>
                </li>
              </ul>

              <div
                v-if="yieldSettings.MinPeople > 0"
                class="field has-addons is-pulled-right"
              >
                <p class="control">
                  <InputNumeric
                    :allowNegativeValue="false"
                    v-model.number="yieldSettings.MinPeople"
                  />
                </p>
              </div>
            </td>
          </tr>
          <tr v-if="yieldSettings.MeetingtypeId === 1">
            <td>{{ $t('Components.Yield.ModalCreateYieldSettings.MaxPeople') }}</td>
            <td>
              <ul class="tabs is-toggle is-inline-flex is-small">
                <li :class="{
                    'is-active': yieldSettings.MaxPeople === 0,
                  }">
                  <a @click="yieldSettings.MaxPeople = 0">
                    <span>{{ $t(`General.State_Off`) }}</span>
                  </a>
                </li>
                <li :class="{
                    'is-active': yieldSettings.MaxPeople > 0,
                  }">
                  <a @click="yieldSettings.MaxPeople = 100">
                    <span>{{ $t(`General.State_On`) }}</span>
                  </a>
                </li>
              </ul>

              <div
                v-if="yieldSettings.MaxPeople > 0"
                class="field has-addons is-pulled-right"
              >
                <p class="control">
                  <InputNumeric
                    :allowNegativeValue="false"
                    v-model.number="yieldSettings.MaxPeople"
                  />
                </p>
              </div>
            </td>
          </tr>

          <tr
            v-if="yieldSettings.CalculationType !== 'PerHourPerSeat'"
            class="has-text-vertical-middle"
          >
            <td>
              {{
                $t(
                  `Components.Yield.ModalCreateYieldSettings.Label_NrOfHoursPerDayPart`
                )
              }}
            </td>
            <td>
              <div class="select">
                <select
                  v-model="yieldSettings.HoursPerDayPart"
                  @change="setPriceParts"
                >
                  <option
                    v-for="dayPart in dayParts"
                    :key="dayPart"
                    :value="dayPart"
                    :selected="dayPart === yieldSettings.HoursPerDayPart"
                  >
                    {{ dayPart }}
                  </option>
                </select>
              </div>
            </td>
          </tr>

          <tr v-if="yieldSettings.CalculationType === 'PerHourPerSeat'">
            <td>
              {{ $t(`Components.Yield.ModalCreateYieldSettings.Label_Prices`) }}
            </td>
            <td>
              <div class="box">
                <div
                  v-if="yieldSettings.PriceParts.length === 0"
                  class="has-margin-bottom"
                >
                  <Message>
                    {{ $t(`Components.Yield.ModalCreateYieldSettings.Message_NoPricePartAdded`) }}
                  </Message>
                </div>
                <table class="table is-fullwidth is-striped">
                  <tbody>
                    <tr
                      v-for="(pricePart, index) in yieldSettings.PriceParts"
                      :key="index"
                      class="has-text-vertical-middle"
                    >
                      <td>
                        <span v-if="
                            yieldSettings.CalculationType === 'PerHourPerSeat'
                          ">{{
                            $t(
                              `Components.Yield.ModalCreateYieldSettings.Label_Hour`
                            )
                          }}
                        </span>
                        <span>{{ pricePart.Part }}</span>
                        <span v-if="
                            yieldSettings.CalculationType !== 'PerHourPerSeat'
                          ">
                          {{
                            $tc(
                              `Components.Yield.ModalCreateYieldSettings.Label_DayParts`,
                              pricePart.Part
                            )
                          }}</span>
                      </td>
                      <td>
                        <div class="is-flex is-align-items-center">
                          <div class="field-price field has-addons is-inline-flex is-marginless">
                            <p class="control">
                              <a
                                class="button is-static"
                                v-html="locationState.CurrencySymbol"
                              ></a>
                            </p>
                            <p class="control">
                              <InputNumeric
                                :allowNegativeValue="false"
                                v-model.number="pricePart.Price"
                              />
                            </p>
                          </div>
                          <a
                            @click="removePricePart(index)"
                            class="has-text-danger has-margin-left"
                          >
                            <span class="icon">
                              <font-awesome-icon :icon="['fas', 'trash-alt']" />
                            </span>
                          </a>
                        </div>
                      </td>
                    </tr>
                  </tbody>

                  <!-- Toevoegen -->
                  <tfoot>
                    <tr class="has-text-vertical-middle">
                      <td>
                        <span v-if="
                            yieldSettings.CalculationType === 'PerHourPerSeat'
                          ">{{
                            $t(
                              `Components.Yield.ModalCreateYieldSettings.Label_Hour`
                            )
                          }}
                        </span>
                        <span>{{ yieldSettings.PriceParts.length + 1 }}</span>
                        <span v-if="
                            yieldSettings.CalculationType !== 'PerHourPerSeat'
                          ">
                          {{
                            $tc(
                              `Components.Yield.ModalCreateYieldSettings.Label_DayParts`,
                              pricePart.Part
                            )
                          }}</span>
                      </td>
                      <td>
                        <div class="is-inline">
                          <div class="field-price is-inline-flex field has-addons is-marginless">
                            <p class="control">
                              <a
                                class="button is-static"
                                v-html="locationState.CurrencySymbol"
                              ></a>
                            </p>
                            <p class="control">
                              <InputNumeric
                                :allowNegativeValue="false"
                                v-model.number="newPricePart"
                              />
                            </p>
                          </div>
                        </div>
                        <a
                          @click="addPricePart"
                          class="button has-margin-left is-success"
                        >
                          <span class="icon">
                            <font-awesome-icon :icon="['fas', 'plus']" />
                          </span>
                          <span v-text="$t('Form.Control.Add')"></span>
                        </a>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </td>
          </tr>
          <tr v-if="!showDynamicPricing">
            <td>
              {{
                $t(
                  'Components.Yield.ModalCreateYieldSettings.Label_UseLocationUtilization'
                )
              }}
            </td>
            <td>
              <div class="tabs is-toggle is-small">
                <ul>
                  <li :class="{
                      'is-active': yieldSettings.ApplyLocationUtilization,
                    }">
                    <a @click="yieldSettings.ApplyLocationUtilization = true">
                      <span>{{ $t('General.Yes') }}</span>
                    </a>
                  </li>
                  <li :class="{
                      'is-active': !yieldSettings.ApplyLocationUtilization,
                    }">
                    <a @click="yieldSettings.ApplyLocationUtilization = false">
                      <span>{{ $t('General.No') }}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </td>
          </tr>
          <tr v-if="!showDynamicPricing">
            <td>
              {{
                $t(
                  'Components.Yield.ModalCreateYieldSettings.Label_UseSpaceUtilization'
                )
              }}
            </td>
            <td>
              <div class="tabs is-toggle is-small">
                <ul>
                  <li :class="{
                      'is-active': yieldSettings.ApplySpaceUtilization,
                    }">
                    <a @click="yieldSettings.ApplySpaceUtilization = true">
                      <span>{{ $t('General.Yes') }}</span>
                    </a>
                  </li>
                  <li :class="{
                      'is-active': !yieldSettings.ApplySpaceUtilization,
                    }">
                    <a @click="yieldSettings.ApplySpaceUtilization = false">
                      <span>{{ $t('General.No') }}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </td>
          </tr>
          <tr v-if="!showDynamicPricing">
            <td>
              {{
                $t(
                  'Components.Yield.ModalCreateYieldSettings.Label_UseDayPercentage'
                )
              }}
            </td>
            <td>
              <div class="tabs is-toggle is-small">
                <ul>
                  <li :class="{ 'is-active': yieldSettings.ApplyDayPercentage }">
                    <a @click="yieldSettings.ApplyDayPercentage = true">
                      <span>{{ $t('General.Yes') }}</span>
                    </a>
                  </li>
                  <li :class="{ 'is-active': !yieldSettings.ApplyDayPercentage }">
                    <a @click="yieldSettings.ApplyDayPercentage = false">
                      <span>{{ $t('General.No') }}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </td>
          </tr>
          <tr v-if="!showDynamicPricing">
            <td>
              {{
                $t(
                  'Components.Yield.ModalCreateYieldSettings.Label_UseGroupSize'
                )
              }}
            </td>
            <td>
              <div class="tabs is-toggle is-small">
                <ul>
                  <li :class="{ 'is-active': yieldSettings.ApplyGroupSize }">
                    <a @click="yieldSettings.ApplyGroupSize = true">
                      <span>{{ $t('General.Yes') }}</span>
                    </a>
                  </li>
                  <li :class="{ 'is-active': !yieldSettings.ApplyGroupSize }">
                    <a @click="yieldSettings.ApplyGroupSize = false">
                      <span>{{ $t('General.No') }}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </ui-component-modal>
</template>

<script>
import Vue from 'vue'
import { mapState, mapMutations } from 'vuex'
import channelProvider from '@/providers/channel'
import spaceProvider from '@/providers/space'
import yieldProvider from '@/providers/yield'
import BaseDateField from '@/components/UI/Form/BaseDateField'
import InputNumeric from '@/components/UI/Form/InputNumeric'

const Message = () =>
  import(
    /* webpackChunkName: "settings-spaces-list" */
    '@/components/UI/Message'
  )

export default {
  components: {
    Message,
    BaseDateField,
    InputNumeric,
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },

    onClickCancel: {
      type: Function,
      required: true,
    },

    channelId: {
      Type: Number,
      default: 0,
    },

    meetingtypeId: {
      Type: Number,
      default: 0,
    },

    spaceId: {
      Type: Number,
      default: 0,
    },

    voucherId: {
      Type: Number,
      default: 0,
    },
    showDynamicPricing: {
      Type: Boolean,
      default: true,
    },
    firstRunView: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      startDate: new Date(),
      channel: null,
      isLoadingSpaces: false,
      isSaving: false,
      isSavingSuccess: false,
      isSavingError: false,
      locationId: Number(this.$route.params.locationId),
      meetingtypes: [],
      spaces: [],
      dayParts: [1, 2, 3, 4, 5, 6, 12],
      yieldSettings: {
        LocationId: Number(this.$route.params.locationId),
        ChannelId: this.channelId,
        MeetingtypeId: this.meetingtypeId,
        SpaceId: this.spaceId,
        VoucherId: this.voucherId,
        TaxPercentage: 0,
        Deductible: true,
        ValidFrom: new Date(),
        Status: 'Draft',
        CalculationType: 'PerHourPerSeat',
        HoursPerDayPart: 1,
        ApplyLocationUtilization: false,
        ApplySpaceUtilization: false,
        ApplyDayPercentage: false,
        ApplyGroupSize: false,
        PriceParts: [],
        MinPeople: 0,
        MaxPeople: 0,
      },
      newPricePart: 0,
      calculationTypeLabels: {
        PerHourPerSeat: this.$t(
          `Components.Yield.ModalCreateYieldSettings.Label_PricePHourPSeat`
        ),
        PerDayPartOnly: this.$t(
          `Components.Yield.ModalCreateYieldSettings.Label_PricePDayPart`
        ),
        PerDayPartPerPerson: this.$t(
          `Components.Yield.ModalCreateYieldSettings.Label_PricePDayPartPPerson`
        ),
      },
    }
  },

  computed: {
    ...mapState('locationStore', ['locationState']),
    ...mapState('spaceStore', ['space']),
    ...mapState('voucherStore', ['voucher']),

    channelName() {
      if (this.locationState) {
        let channelIndex = this.locationState.Channels.findIndex(
          (c) => c.ChannelId === this.channelId
        )
        if (channelIndex > -1) {
          return this.locationState.Channels[channelIndex].ChannelName
        }
      }
      return ''
    },

    disableSave() {
      return this.yieldSettings.MeetingtypeId === 0 && this.yieldSettings.VoucherId === 0
    }
  },

  created() {
    if (this.voucherId === 0 && this.spaceId === 0) {
      this.getLocationSpaces()
    }

    if (this.voucher) {
      this.yieldSettings.ValidFrom = this.voucher.ValidFrom
    }

    if (this.voucher && this.voucher.ChannelId > 0) {
      this.yieldSettings.ChannelId = this.voucher.ChannelId
    }

    if (this.yieldSettings.ChannelId > 0) {
      this.getChannelData()
    }

    this.getMeetingtypes()

    if (!this.channel) {
      let defaultChannelId = this.locationState.Settings.ChannelId
      let channelIndex = this.locationState.Channels.findIndex(
        (lc) => lc.ChannelId === defaultChannelId
      )
      if (channelIndex > -1) {
        this.channel = this.locationState.Channels[channelIndex]
      }
    }
  },

  methods: {
    getChannelData() {
      channelProvider.methods
        .getChannelById(this.yieldSettings.ChannelId)
        .then((response) => {
          if (response.status === 200) {
            this.channel = response.data
          }
        })
        .catch((err) => { })
    },

    getLocationSpaces() {
      let self = this

      if (!self.isLoadingSpaces) {
        self.isLoadingSpaces = true

        spaceProvider.methods.getLocationSpaces(self.locationId, '', 0, 0, 0, 100)
          .then((response) => {
            if (response.status === 200) {
              self.spaces = response.data.Results
            }
          })
          .finally(() => {
            self.isLoadingSpaces = false
          })
      }
    },

    getMeetingtypes() {
      if (this.voucher) {
        this.meetingtypes = this.voucher.MeetingtypeIds
      } else {
        this.meetingtypes = this.locationState.Meetingtypes.map(
          (m) => m.MeetingtypeId
        )
      }
    },

    meetingtypeSelected() {
      let meetingtype = this.locationState.Meetingtypes.find(
        (mt) => mt.MeetingtypeId === this.yieldSettings.MeetingtypeId
      )
      if (meetingtype) {
        this.yieldSettings.TaxPercentage = meetingtype.DefaultSpaceVat
      }
    },

    setPriceParts() {
      this.yieldSettings.PriceParts = []
      let nrOfParts = 12 / this.yieldSettings.HoursPerDayPart

      for (let i = 0; i < nrOfParts; i++) {
        this.yieldSettings.PriceParts.push({
          ChannelId: this.yieldSettings.ChannelId,
          LocationId: this.yieldSettings.LocationId,
          MeetingtypeId: this.yieldSettings.MeetingtypeId,
          SpaceId: 0,
          Part: i + 1,
          Price: 0,
        })
      }
    },

    addPricePart() {
      let existingPriceParts = this.yieldSettings.PriceParts.length

      this.yieldSettings.PriceParts.push({
        ChannelId: this.yieldSettings.ChannelId,
        LocationId: this.yieldSettings.LocationId,
        MeetingtypeId: this.yieldSettings.MeetingtypeId,
        SpaceId: 0,
        Part: existingPriceParts + 1,
        Price: this.newPricePart,
      })

      this.newPricePart = 0
    },

    removePricePart(index) {
      let self = this

      self.yieldSettings.PriceParts.splice(index, 1)

      for (let i = 0; i < self.yieldSettings.PriceParts.length; i++) {
        self.yieldSettings.PriceParts[i].Part = i + 1
      }
    },

    createYieldSetting() {
      let self = this

      if (!self.isSaving && !this.disableSave) {
        self.isSaving = true

        if (this.newPricePart > 0) {
          // Add new price part when user forgot to press on the add button
          this.addPricePart()
        }

        if (this.yieldSettings.MeetingtypeId === 2) {
          this.yieldSettings.MinPeople = 0
          this.yieldSettings.MaxPeople = 0
        }

        self.yieldSettings.ValidFrom =
          self.$options.filters.dateObjectIsoDateString(self.startDate)

        yieldProvider.methods
          .createLocationYieldSettings(self.yieldSettings)
          .then((response) => {
            if (response.status === 201) {
              this.$emit('reload')
              self.isSavingSuccess = true

              setTimeout(() => {
                self.onClickCancel()
                self.$emit('yieldSettingCreated', response.data)
              }, 1500)
            }
          })
          .catch((error) => {
            self.isSavingError = true
          })
          .finally(() => {
            self.isSaving = false
          })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.table-settings {
  > tbody > tr > td:first-child {
    width: 210px;
  }
}

.customWidth {
  display: inline-flex !important;
  width: auto;
}
</style>
